import { useEffect, useContext } from 'react'
import { withRouter } from 'react-router-dom'
import { ContentBody, ContentHeader } from 'components/shared/content'
import * as H from 'history'
import { ExTable, TableBody, TableCell, TableHead, TableWrapper } from 'components/shared/widget'
import { PcrTestRun, PcrTestRunSearchForm } from 'entities/pcr_test_run'
import { useCalculationTestResultApi, useFetchPcrTestRunsApi } from 'api/pcr_test_run'
import { pcrTestRunColumnInfoList, PcrTestRunTableData } from './pcr_test_run_table'
import { TableHeads, useDisplaySetting } from 'components/shared/display_setting'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useEffectSkipFirst, useForm, useQuery } from 'utils/hooks'
import { Pagination } from 'components/shared/pagination'
import { EditPcrTestSamplesForm, NewPcrTestRunForm } from './pcr_test_run_form'
import { useState } from 'react'
import { SearchConditionView } from 'components/shared/search_drawer'
import { Button } from 'react-bootstrap'
import { ProcessLogsModal } from 'components/pcr_run/process_log_modal'

type Props = {
    history: H.History
}

const PcrTestRunsPage = (props: Props) => {
    const query = useQuery<PcrTestRunSearchForm>()
    const searchForm = useForm<PcrTestRunSearchForm>({ ordering: '-pcr_test_run_created_at', ...query })
    const listApi = useFetchPcrTestRunsApi(searchForm)
    const globalState = useContext(GlobalStateContext)
    const pcrTestRunDisplaySetting = useDisplaySetting(pcrTestRunColumnInfoList(searchForm), 'PcrTestRun')
    const [showEdit, setShowEdit] = useState<boolean>(false)
    const [selectedPcrTestRun, setSelectedPcrTestRun] = useState<PcrTestRun>({})
    const calcApi = useCalculationTestResultApi()

    useEffect(() => {
        listApi.execute()
    }, [false])

    const handleClickRemoveFilter = (key: keyof PcrTestRunSearchForm): void => {
        searchForm.update((f) => {
            delete f[key]
        })
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(calcApi.loading)
        if (calcApi.isSuccess()) {
            listApi.execute()
        }
    }, [calcApi.loading])

    return (
        <>
            <EditPcrTestSamplesForm
                show={showEdit}
                pcrTestRun={selectedPcrTestRun}
                onClose={() => setShowEdit(false)}
                onComplete={() => {
                    setShowEdit(true)
                    listApi.execute()
                }}
            />
            <ContentHeader
                headerTitle="テストラン"
                left={
                    <>
                        <NewPcrTestRunForm onComplete={() => listApi.execute()} />
                        <SearchConditionView searchForm={searchForm} onClickRemoveButton={handleClickRemoveFilter} />
                    </>
                }
                right={
                    <>
                        <Pagination pageSet={listApi.pageSet} response={listApi.response} searchForm={searchForm}></Pagination>
                    </>
                }
            />
            <ContentBody>
                <TableWrapper>
                    <ExTable className="sticky-table">
                        <TableHead>
                            <tr>
                                <TableHeads displaySetting={pcrTestRunDisplaySetting} />
                                <TableCell th width={150}>
                                    条件入力
                                </TableCell>
                                <TableCell th width={120}>
                                    出力
                                </TableCell>
                                <TableCell th width={150}>
                                    結果ログ
                                </TableCell>
                                <TableCell th width={150}>
                                    結果票
                                </TableCell>
                            </tr>
                        </TableHead>
                        <TableBody loading={listApi.loading}>
                            {listApi.response.results.map((pcrTestRun: PcrTestRun) => {
                                return (
                                    <tr key={pcrTestRun.id}>
                                        <PcrTestRunTableData pcrTestRun={pcrTestRun} displaySetting={pcrTestRunDisplaySetting} />
                                        <TableCell>
                                            <Button
                                                variant="secondary"
                                                onClick={() => {
                                                    setShowEdit(true)
                                                    setSelectedPcrTestRun(pcrTestRun)
                                                }}
                                            >
                                                検体条件
                                            </Button>
                                        </TableCell>
                                        <TableCell>
                                            {pcrTestRun.pcrTestSamples?.every((pts) => !!pts.age && !!pts.gender && !!pts.productItem) && (
                                                <Button
                                                    variant="success"
                                                    onClick={() => {
                                                        calcApi.execute(pcrTestRun.id!)
                                                    }}
                                                >
                                                    結果出力
                                                </Button>
                                            )}
                                        </TableCell>

                                        <TableCell>
                                            {pcrTestRun.processLogsJson && (
                                                <ProcessLogsModal
                                                    pcrProcessLogs={JSON.parse(pcrTestRun.processLogsJson)}
                                                    onClickShowAll={() => props.history.push('/pcr_process_logs')}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {pcrTestRun.pcrTestSamples?.filter((pts) => !!pts.pcrTestResult).length !== 0 && (
                                                <Button
                                                    variant="primary"
                                                    onClick={() => {
                                                        props.history.push(`pcr_test_runs/${pcrTestRun.id}/results`)
                                                    }}
                                                >
                                                    結果票表示
                                                </Button>
                                            )}
                                        </TableCell>
                                    </tr>
                                )
                            })}
                        </TableBody>
                    </ExTable>
                    <Pagination pageSet={listApi.pageSet} response={listApi.response}></Pagination>
                </TableWrapper>
            </ContentBody>
        </>
    )
}

export default withRouter(PcrTestRunsPage)
